/**
 * Token validation utilities for JWT token expiration checking
 */

export interface TokenPayload {
  sub: string;
  email: string;
  exp?: number;
  iat?: number;
  organizationId?: string;
  organizationRole?: string;
  [key: string]: any;
}

/**
 * Decode a JWT token without verifying the signature
 * @param token JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[TokenValidator] Invalid token format');
      return null;
    }
    
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error('[TokenValidator] Error decoding token:', error);
    return null;
  }
}

/**
 * Check if a token is expired
 * @param token JWT token string
 * @returns true if expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  
  if (!payload || !payload.exp) {
    // If no expiration, consider it valid (though this shouldn't happen)
    return false;
  }
  
  const now = Date.now() / 1000;
  return payload.exp < now;
}

/**
 * Get the time remaining until token expiration in seconds
 * @param token JWT token string
 * @returns Seconds until expiration, or -1 if expired, or Infinity if no expiration
 */
export function getTokenTimeRemaining(token: string): number {
  const payload = decodeToken(token);
  
  if (!payload || !payload.exp) {
    return Infinity;
  }
  
  const now = Date.now() / 1000;
  const remaining = payload.exp - now;
  
  return remaining > 0 ? remaining : -1;
}

/**
 * Check if a token should be refreshed (expires within threshold)
 * @param token JWT token string
 * @param thresholdSeconds Seconds before expiration to trigger refresh (default 300 = 5 minutes)
 * @returns true if should refresh, false otherwise
 */
export function shouldRefreshToken(token: string, thresholdSeconds: number = 300): boolean {
  const remaining = getTokenTimeRemaining(token);
  
  if (remaining === -1) {
    // Already expired
    return true;
  }
  
  if (remaining === Infinity) {
    // No expiration
    return false;
  }
  
  return remaining < thresholdSeconds;
}

/**
 * Extract user information from token
 * @param token JWT token string
 * @returns User info or null if invalid
 */
export function getUserFromToken(token: string) {
  const payload = decodeToken(token);
  
  if (!payload) {
    return null;
  }
  
  return {
    id: payload.sub,
    email: payload.email,
    organizationId: payload.organizationId,
    organizationRole: payload.organizationRole,
  };
}

/**
 * Validate token and get status
 * @param token JWT token string
 * @returns Token validation status
 */
export function validateToken(token: string): {
  valid: boolean;
  expired: boolean;
  shouldRefresh: boolean;
  timeRemaining: number;
  payload: TokenPayload | null;
} {
  const payload = decodeToken(token);
  const expired = isTokenExpired(token);
  const shouldRefresh = shouldRefreshToken(token);
  const timeRemaining = getTokenTimeRemaining(token);
  
  return {
    valid: !!payload && !expired,
    expired,
    shouldRefresh,
    timeRemaining,
    payload,
  };
}